import { MailOpen, X } from "lucide-react";
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import toast from "react-hot-toast";

const OrdersList = () => {
  const [orders, setOrders] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [deletingOrderId, setDeletingOrderId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch orders
        const ordersResponse = await fetch("https://allyoutube-01-02d59d78beb5.herokuapp.com/orders");
        if (!ordersResponse.ok) {
          throw new Error(`HTTP error! Status: ${ordersResponse.status}`);
        }
        const ordersData = await ordersResponse.json();

        // Fetch packages
        const packagesResponse = await fetch("https://allyoutube-01-02d59d78beb5.herokuapp.com/packages");
        if (!packagesResponse.ok) {
          throw new Error(`HTTP error! Status: ${packagesResponse.status}`);
        }
        const packagesData = await packagesResponse.json();

        // Set the fetched data
        setOrders(ordersData);
        setPackages(packagesData);
        setError(null);
      } catch (err) {
        setError(`Failed to fetch data: ${err.message}`);
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Get package name by ID
  const getPackageName = (packageId) => {
    const packageItem = packages.find((pkg) => pkg._id === packageId);
    return packageItem ? packageItem.packageName : "N/A";
  };

  // Open package modal
  const openPackageModal = (packageId) => {
    const packageItem = packages.find((pkg) => pkg._id === packageId);
    if (packageItem) {
      setSelectedPackage(packageItem);
      setShowModal(true);
    }
  };

  // Close package modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedPackage(null);
  };

  // Calculate days left in membership
  const calculateDaysLeft = (validityDate) => {
    if (!validityDate) return "N/A";

    const today = new Date();
    const endDate = new Date(validityDate);
    const timeDiff = endDate - today;
    const daysLeft = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    return daysLeft > 0 ? daysLeft : "Expired";
  };

  // Get date value for sorting from order object
  const getOrderDate = (order) => {
    // Try different date fields, with a fallback
    return order.createdAt || order.orderDate || order.date || "1970-01-01";
  };

  // Format date to readable format
  const formatDate = (dateString) => {
    try {
      if (!dateString) return "N/A";
      
      // Create a Date object from the string
      const date = new Date(dateString);
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        // If it's not a valid ISO date, try to handle other formats
        if (dateString.includes('/')) {
          // Handle formats like "21/09/2025"
          const parts = dateString.split('/');
          if (parts.length === 3) {
            const [day, month, year] = parts;
            // Create date in YYYY-MM-DD format
            const formattedDate = new Date(`${year}-${month}-${day}`);
            if (!isNaN(formattedDate.getTime())) {
              return formattedDate.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              });
            }
          }
        }
        return dateString; // Return original if parsing fails
      }
      
      // Format valid ISO date
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      console.error("Date formatting error:", error);
      return dateString;
    }
  };

  // Sorting function
  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Get sorted and filtered orders
  const getSortedAndFilteredOrders = () => {
    let filteredOrders = [...orders];

    // Apply search filter
    if (searchTerm) {
      filteredOrders = filteredOrders.filter(
        (order) =>
          order.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.orderStatus?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          getPackageName(order.packageId)
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      filteredOrders.sort((a, b) => {
        if (sortConfig.key === "createdAt" || sortConfig.key === "date") {
          // Handle date sorting specifically
          const dateA = new Date(getOrderDate(a));
          const dateB = new Date(getOrderDate(b));
          return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
        }

        if (sortConfig.key === "daysLeft") {
          const daysLeftA = calculateDaysLeft(a.MembershipValidity);
          const daysLeftB = calculateDaysLeft(b.MembershipValidity);

          // Handle 'Expired' case
          if (daysLeftA === "Expired" && daysLeftB !== "Expired")
            return sortConfig.direction === "asc" ? -1 : 1;
          if (daysLeftA !== "Expired" && daysLeftB === "Expired")
            return sortConfig.direction === "asc" ? 1 : -1;
          if (daysLeftA === "Expired" && daysLeftB === "Expired") return 0;

          return sortConfig.direction === "asc"
            ? daysLeftA - daysLeftB
            : daysLeftB - daysLeftA;
        }

        if (sortConfig.key === "packageName") {
          const packageNameA = getPackageName(a.packageId);
          const packageNameB = getPackageName(b.packageId);

          if (packageNameA < packageNameB) {
            return sortConfig.direction === "asc" ? -1 : 1;
          }
          if (packageNameA > packageNameB) {
            return sortConfig.direction === "asc" ? 1 : -1;
          }
          return 0;
        }

        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredOrders;
  };

  // Get current orders for pagination
  const sortedAndFilteredOrders = getSortedAndFilteredOrders();
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = sortedAndFilteredOrders.slice(
    indexOfFirstOrder,
    indexOfLastOrder
  );
  const totalPages = Math.ceil(sortedAndFilteredOrders.length / ordersPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Function to determine badge color based on order status
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Completed":
        return "bg-success";
      case "Processing":
        return "bg-warning text-dark";
      case "Shipped":
        return "bg-info text-dark";
      case "Cancelled":
        return "bg-danger";
      case "Paid":
        return "bg-success";
      case "pending":
        return "bg-warning text-dark";
      default:
        return "bg-secondary";
    }
  };

  // Function to determine badge color based on days left
  const getValidityBadgeClass = (daysLeft) => {
    if (daysLeft === "Expired") return "bg-danger";
    if (daysLeft === "N/A") return "bg-secondary";

    const days = parseInt(daysLeft);
    if (days <= 3) return "bg-danger";
    if (days <= 7) return "bg-warning text-dark";
    return "bg-success";
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? " ↑" : " ↓";
    }
    return "";
  };

  const handleDeleteOrder = async (orderId) => {
    setDeletingOrderId(orderId);
    try {
      const res = await fetch(`https://allyoutube-01-02d59d78beb5.herokuapp.com/order/${orderId}`, {
        method: "DELETE",
      });
      const data = await res.json();

      if (data.success) {
        setOrders((prevOrders) => prevOrders.filter((o) => o._id !== orderId));
        toast.success("Order deleted successfully!");
      } else {
        toast.error("Failed to delete order.");
      }
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Something went wrong while deleting.");
    } finally {
      setDeletingOrderId(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>Orders Management - View and Manage Customer Orders</title>
        <meta
          name="description"
          content="Easily view, search, and manage all customer orders with real-time tracking, payment status, package details, and membership validity."
        />
      </Helmet>
      <div className="container py-4">
        <div className="row mb-4">
          <div className="col">
            <h2 className="fw-bold" style={{ color: "#ffffff" }}>
              Orders Management
            </h2>
            <p className="text-muted">View and manage all customer orders</p>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col">
            <div
              className="card shadow-sm border-0"
              style={{ borderRadius: "10px" }}
            >
              <div
                className="card-header bg-white d-flex justify-content-between align-items-center py-3"
                style={{ borderBottom: "1px solid rgba(0,0,0,0.08)" }}
              >
                <h5 className="mb-0 fw-bold" style={{ color: "#3a3a3a" }}>
                  All Orders
                </h5>
                <div className="d-flex">
                  <div
                    className="input-group me-2"
                    style={{ maxWidth: "250px" }}
                  >
                    <input
                      type="text"
                      className="form-control border-end-0"
                      placeholder="Search orders..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1); // Reset to first page on search
                      }}
                      style={{ borderRadius: "5px 0 0 5px" }}
                    />
                    <span
                      className="input-group-text bg-white border-start-0"
                      style={{ borderRadius: "0 5px 5px 0" }}
                    >
                      <i className="fa fa-search text-muted"></i>
                    </span>
                  </div>
                </div>
              </div>
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden"></span>
                    </div>
                    <p className="mt-2 text-muted">Loading orders...</p>
                  </div>
                ) : error ? (
                  <div className="alert alert-danger m-3" role="alert">
                    {error}
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table
                        className="table table-hover mb-0"
                        style={{
                          borderCollapse: "separate",
                          borderSpacing: "0",
                        }}
                      >
                        <thead>
                          <tr className="bg-light">
                            <th
                              className="px-4 py-3"
                              style={{ cursor: "pointer" }}
                              onClick={() => requestSort("orderId")}
                            >
                              Order ID {getSortIndicator("orderId")}
                            </th>
                            <th
                              className="px-4 py-3"
                              style={{ cursor: "pointer" }}
                              onClick={() => requestSort("customerName")}
                            >
                              Customer {getSortIndicator("customerName")}
                            </th>
                            <th
                              className="px-4 py-3"
                              style={{ cursor: "pointer" }}
                              onClick={() => requestSort("packageName")}
                            >
                              Package {getSortIndicator("packageName")}
                            </th>
                            <th
                              className="px-4 py-3"
                              style={{ cursor: "pointer" }}
                              onClick={() => requestSort("createdAt")}
                            >
                              Date {getSortIndicator("createdAt")}
                            </th>
                            <th
                              className="px-4 py-3"
                              style={{ cursor: "pointer" }}
                              onClick={() => requestSort("totalPrice")}
                            >
                              Amount {getSortIndicator("totalPrice")}
                            </th>
                            <th
                              className="px-4 py-3"
                              style={{ cursor: "pointer" }}
                              onClick={() => requestSort("paymentStatus")}
                            >
                              Payment {getSortIndicator("paymentStatus")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentOrders.length === 0 ? (
                            <tr>
                              <td
                                colSpan="7"
                                className="text-center py-4 text-muted"
                              >
                                No orders found
                              </td>
                            </tr>
                          ) : (
                            currentOrders.map((order) => {
                              const daysLeft = calculateDaysLeft(
                                order.MembershipValidity
                              );
                              return (
                                <tr
                                  key={order._id}
                                  style={{ transition: "all 0.2s ease" }}
                                >
                                  <td className="px-4 py-3 align-middle fw-bold">
                                    #{order.orderId}
                                  </td>
                                  <td className="px-4 py-3 align-middle">
                                    <div className="d-flex align-items-center">
                                      <div
                                        className="avatar-placeholder bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-2"
                                        style={{
                                          width: "36px",
                                          height: "36px",
                                          fontSize: "16px",
                                        }}
                                      >
                                        {order.customerName.charAt(0)}
                                      </div>
                                      <div>
                                        <div>{order.customerName}</div>
                                        <small className="text-muted">
                                          {order.customerEmail}
                                        </small>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 align-middle">
                                    {order.packageId ? (
                                      <span
                                        className="text-primary fw-bold"
                                        style={{
                                          cursor: "pointer",
                                          textDecoration: "underline",
                                        }}
                                        onClick={() =>
                                          openPackageModal(order.packageId)
                                        }
                                      >
                                        {getPackageName(order.packageId)}
                                      </span>
                                    ) : (
                                      <span className="text-muted">N/A</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 align-middle">
                                    {formatDate(order.createdAt)}
                                  </td>
                                  <td className="px-4 py-3 align-middle fw-bold">
                                    ${order.totalPrice}
                                  </td>
                                  <td className="px-4 py-3 align-middle">
                                    <span
                                      className={`badge ${getStatusBadgeClass(
                                        order.paymentStatus
                                      )} rounded-pill px-3 py-2`}
                                      style={{ fontSize: "0.75rem" }}
                                    >
                                      {order.paymentStatus.toUpperCase()}
                                    </span>
                                    {/* when click the icon delete the data from the collection , i want to showing this icon pending option*/}
                                    {order.paymentStatus.toLowerCase() ===
                                      "pending" &&
                                      (deletingOrderId === order._id ? (
                                        <div
                                          className="spinner-border spinner-border-sm text-danger ms-2"
                                          role="status"
                                        >
                                          <span className="visually-hidden">
                                            Deleting...
                                          </span>
                                        </div>
                                      ) : (
                                        <X
                                          className="ms-2"
                                          color="red"
                                          style={{ cursor: "pointer" }}
                                          onClick={() =>
                                            handleDeleteOrder(order._id)
                                          }
                                        />
                                      ))}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <div className="d-flex justify-content-between align-items-center p-3 border-top">
                      <div className="text-muted">
                        Showing {indexOfFirstOrder + 1} to{" "}
                        {Math.min(
                          indexOfLastOrder,
                          sortedAndFilteredOrders.length
                        )}{" "}
                        of {sortedAndFilteredOrders.length} entries
                      </div>
                      <nav aria-label="Page navigation">
                        <ul className="pagination mb-0">
                          <li
                            className={`page-item ${
                              currentPage === 1 ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => paginate(currentPage - 1)}
                              disabled={currentPage === 1}
                              style={{ border: "none" }}
                            >
                              Previous
                            </button>
                          </li>
                          {Array.from({ length: totalPages }).map(
                            (_, index) => (
                              <li
                                key={index}
                                className={`page-item ${
                                  currentPage === index + 1 ? "active" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => paginate(index + 1)}
                                  style={{ border: "none" }}
                                >
                                  {index + 1}
                                </button>
                              </li>
                            )
                          )}
                          <li
                            className={`page-item ${
                              currentPage === totalPages ? "disabled" : ""
                            }`}
                          >
                            <button
                              className="page-link"
                              onClick={() => paginate(currentPage + 1)}
                              disabled={currentPage === totalPages}
                              style={{ border: "none" }}
                            >
                              Next
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Package Modal */}
        {showModal && selectedPackage && (
          <div
            className="modal fade show"
            tabIndex="-1"
            role="dialog"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {selectedPackage.packageName} Package Details
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeModal}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="card border-0 shadow-sm">
                    <div className="card-body">
                      <div className="text-center mb-4">
                        <h3 className="display-6 fw-bold text-primary">
                          ${selectedPackage.price}
                        </h3>
                        <p className="text-muted mb-0">
                          {selectedPackage.credit} credits
                        </p>
                      </div>

                      <ul className="list-group list-group-flush">
                        <li className="list-group-item border-0 py-2">
                          <i className="fa fa-check text-success me-2"></i>{" "}
                          {selectedPackage.pointOne}
                        </li>
                        <li className="list-group-item border-0 py-2">
                          <i className="fa fa-check text-success me-2"></i>{" "}
                          {selectedPackage.pointTwo}
                        </li>
                        <li className="list-group-item border-0 py-2">
                          <i className="fa fa-check text-success me-2"></i>{" "}
                          {selectedPackage.pointThree}
                        </li>
                        <li className="list-group-item border-0 py-2">
                          <i className="fa fa-check text-success me-2"></i>{" "}
                          {selectedPackage.pointFour}
                        </li>
                        <li className="list-group-item border-0 py-2">
                          <i className="fa fa-check text-success me-2"></i>{" "}
                          {selectedPackage.pointFive}
                        </li>
                        <li className="list-group-item border-0 py-2">
                          <i className="fa fa-check text-success me-2"></i>{" "}
                          {selectedPackage.pointSix}
                        </li>
                      </ul>

                      <div className="d-flex justify-content-between mt-3">
                        <small className="text-muted">
                          Package ID: {selectedPackage._id}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeModal}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal Backdrop for clicking outside */}
        {showModal && (
          <div className="modal-backdrop fade show" onClick={closeModal}></div>
        )}
      </div>
    </>
  );
};

export default OrdersList;